import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { optimizeImageUrl, responsiveWidth } from "@/lib/imageUtils";
import ServiceForm from "@/components/ServiceForm";
import FAQSection from "@/components/FAQSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import SEOHead from "@/components/SEOHead";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("INR");

  useEffect(() => {
    api.get("/geo/currency").then((res) => setCurrency(res.data.currency)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${slug}`).then((res) => setService(res.data)).catch(() => setService(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={32} /></div>;
  }
  if (!service) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">Service not found.</div>;
  }

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": service.name,
      "description": service.short_description,
      "image": service.banner_image || undefined,
      "provider": { "@type": "TravelAgency", "name": "Gulfudaan", "url": window.location.origin },
      "areaServed": "IN",
      "offers": service.requires_payment && service.price_inr != null ? {
        "@type": "Offer",
        "price": service.price_inr,
        "priceCurrency": "INR",
        "availability": "https://schema.org/InStock",
      } : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
        { "@type": "ListItem", "position": 2, "name": "Services", "item": window.location.origin },
        { "@type": "ListItem", "position": 3, "name": service.name, "item": window.location.href },
      ],
    },
  ];
  if (service.faqs?.length > 0) {
    schema.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": service.faqs.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
  }

  return (
    <div data-testid="service-detail-page">
      <SEOHead pageKey={service.slug} schema={schema} />
      <section data-testid="service-banner" className="relative h-[20vh] sm:h-[42vh] lg:h-[50vh] min-h-[160px] sm:min-h-[320px] lg:min-h-[380px] flex items-center overflow-hidden">
        <img src={optimizeImageUrl(service.banner_image, responsiveWidth(1600))} alt={service.name} fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-4xl lg:text-5xl text-white font-bold">{service.name}</h1>
          <p className="hidden sm:block text-gray-200 mt-4 max-w-2xl mx-auto">{service.short_description}</p>
        </div>
      </section>

      <section className="py-8 sm:py-20 bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          <div className="order-2 lg:order-1">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--brand-primary)]">About This Service</span>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#1A1A1A] mt-3 mb-4">{service.name}</h2>
            <p className="text-[var(--brand-secondary)] leading-relaxed mb-8">{service.long_description}</p>

            {service.slug !== "gamca-cancellation" && (
            <div data-testid="service-pricing-section" className="p-6 rounded-lg border border-[#E5E1DA] bg-white">
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">Pricing</h3>
            {service.requires_payment ? (
                <div className="flex items-baseline gap-4">
                  {currency === "USD" && service.price_usd > 0 ? (
                    <span data-testid="service-price-usd" className="font-price text-3xl sm:text-4xl text-[var(--brand-primary)]">${service.price_usd}</span>
                  ) : (
                    <span data-testid="service-price-inr" className="font-price text-3xl sm:text-4xl text-[var(--brand-primary)]">₹{service.price_inr}</span>
                  )}
                </div>
              ) : (
                <p data-testid="service-price-free" className="text-green-700 font-medium">No advance payment required - get a free quotation first!</p>
              )}
            </div>
            )}
            <Link to="/become-agent" data-testid="agent-nudge-strip"
              className="mt-5 flex items-center justify-between gap-3 rounded-lg border border-[color-mix(in_oklab,var(--brand-primary),transparent_65%)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_93%)] px-4 py-3 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_88%)] transition-colors">
              <span className="text-[13px] font-semibold text-[#1A1A1A]">
                Are you a travel agent? <span className="text-[var(--brand-primary)]">Get wholesale B2B rates</span>
              </span>
              <span className="text-[var(--brand-primary)] font-bold text-sm shrink-0">→</span>
            </Link>
          </div>
          <div id="apply" className="order-1 lg:order-2">
            <ServiceForm service={service} onCurrencyChange={setCurrency} />
          </div>
        </div>
      </section>

      <FAQSection faqs={service.faqs} title={`FAQs about ${service.name}`} />
      <TestimonialsSection />

      {service.related_services?.length > 0 && (
        <section data-testid="related-services-section" className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl text-[#1A1A1A] mb-8 text-center">Related Services</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {service.related_services.map((r) => (
                <Link key={r.slug} to={`/services/${r.slug}`} data-testid={`related-service-${r.slug}`}
                  className="p-6 rounded-lg border border-[#E5E1DA] bg-[#FDFCFB] hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <h3 className="font-heading text-lg text-[#1A1A1A] mb-2">{r.name}</h3>
                  <p className="text-sm text-[var(--brand-secondary)] line-clamp-2">{r.short_description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
